import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useWire, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import { useAccessUser } from '@hook/useAccessUser'
import useCheckableItems from '@hook/useCheckableItems'
import useScrollToTop from '@hook/useScrollToTop'
import { CHECK_FIELDS, CHECK_STATUSES_PROCESSING } from '@constants'
import useSortableTableHeaders from '@/hook/useSortableTableHeaders'
import { toast } from 'react-toastify'

import CircleSpinner from '@components/shared/CircleSpinner'
import ChecksTable from './ChecksTable'
import UploadChecksDialog from '@components/shared/Dialogs/UploadChecksDialog'
import Pagination from '@components/shared/Pagination'
import { MdUploadFile } from 'react-icons/md'
import MappingModal, { SOURCE_CHECKS } from './CheckReports/MappingModal'
import SearchInput from '@components/shared/SearchInput'

import './Checks.css'
import { useSearchParams } from 'react-router-dom'
import { updateSearchParam } from '../shared/utils'
import useAbortableFetch from '@/hook/useAbortableFetch'
import useRealtimeCheckStatus from '@/hook/useRealtimeCheckStatus'
import usePollCheckStatus from '@/hook/usePollCheckStatus'

const checksPerPage = 20

const Checks = () => {
    
    const refChecks = useRef()
    
    const [loading, setLoading] = useState(false)
    const [pageIndex, setPageIndex] = useState(0)
    const [unmappedChecks, setUnmappedChecks] = useState([])
    const [checkMappings, setCheckMappings] = useState([])
    const [showMappingsModal, setShowMappingsModal] = useState(false)
    const [mapCheckId, setMapCheckId] = useState('')
    const [searchParams, setSearchParams] = useSearchParams()
    
    const uploadChecksDialogOpen = useWire(store.uploadChecksDialogOpen)
    
    const createCheck = useAccessUser({
        accessType: 'checks',
        accessName: 'create_new_check',
    })
    
    const checks = useWireValue(store.checks)
    const currentOrganization = useWireValue(store.currentOrganization)
    
    const checksInProcessing = useMemo(() => (
        checks?.rows?.filter(it => CHECK_STATUSES_PROCESSING.includes(it?.status)) ?? []
    ), [checks])
    
    const {
        checked,
        allChecked,
        someChecked,
        hasChecked,
        setChecked,
        toggleChecked,
        toggleAllChecked,
    } = useCheckableItems(checks?.rows ?? [])
    
    const {
        orderField,
        orderDirection,
        onHeaderClick,
    } = useSortableTableHeaders(Object.values(CHECK_FIELDS).filter(cf => cf.sortable).map(cf => cf.key))
    
    useEffect(() => {
        setSearchParams(prev => {
            prev.set('field', orderField)
            prev.set('order', orderDirection)
            return prev
        })
    }, [orderDirection, orderField])
    
    useRealtimeCheckStatus()
    usePollCheckStatus(checksInProcessing)
    
    const searchQuery = searchParams.get('q')
    
    const fetchChecks = useCallback(async () => {
        setLoading(true)
        
        await actions.fetchChecks({
            offset: (pageIndex * checksPerPage),
            limit: checksPerPage,
            orderField,
            orderDirection,
            q: searchQuery,
        })
        
        setLoading(false)
        
    }, [pageIndex, orderField, orderDirection, searchQuery])
    
    const processChecks = useCallback(async () => {
        
        if (!checked.length) return
        
        try {
            
            await actions.processChecks(checked)
            
            // Immediately re-fetch the check, since it's status will be reset
            await actions.checkStatuses(checked)
            
            setChecked([])
            toast('Checks queued for processing')
            
        } catch (e) {
            
            console.error('processChecks failed', e)
            toast.error('Failed to process checks, please try again')
            
        }
        
    }, [checked])
    
    const scrollToTop = useScrollToTop(refChecks)
    
    const getUnmappedChecks = async () => {
        if (checks?.rows?.length) {
            try {
                let checkIds = await actions.getUnmappedChecksByIds(checks.rows.map(c => c.id))
                
                setUnmappedChecks(checkIds)
            } catch (err) {
                // no need to warn the user as its an alternative flow
                console.error('getUnmappedChecks failed', err)
            }
        }
    }
    
    const getFieldsById = async id => {
        if (id) {
            const mappings = await actions.getMappingsFiltered({ ids: [id] })
            
            setShowMappingsModal(true)
            setCheckMappings(mappings)
        } else {
            setShowMappingsModal(false)
            setCheckMappings([])
        }
    }
    
    const onFinishMapping = async editedFields => {
        try {
            await actions.saveMappings(Object.values(editedFields))
            await getUnmappedChecks()
            setMapCheckId('')
        } catch (err) {
            toast.error(err.message)
        }
    }
    
    useEffect(() => {
        setPageIndex(0)
    }, [searchParams])
    
    useAbortableFetch(() => { fetchChecks() }, [pageIndex, searchParams, currentOrganization])
    
    useEffect(() => { getUnmappedChecks() }, [checks])
    
    useEffect(() => { getFieldsById(mapCheckId) }, [mapCheckId])
    
    return (
        
        <div ref={refChecks} className="Checks">
            
            <header className="mx-4 mt-2">
                <SearchInput
                    className="w-full grow search-query"
                    value={searchParams.get('q') || ''}
                    placeholder="Search checks..."
                    onChange={e => setSearchParams(updateSearchParam('q', e.target.value))} />
                <button
                    className="flex items-center content-center ml-3 btn btn-primary btn-md btn-secondary shrink-0"
                    disabled={!someChecked && !allChecked}
                    onClick={processChecks}>
                    {/* <MdUploadFile /> */}
                    <span className="ml-2 text-base">Process Checks</span>
                </button>
                <button
                    className="flex items-center content-center ml-3 btn btn-primary btn-md shrink-0"
                    disabled={!createCheck}
                    onClick={() => uploadChecksDialogOpen.setValue(true)}>
                    <MdUploadFile />
                    <span className="ml-2 text-base">Upload Check</span>
                </button>
            </header>
            
            {loading && (
                <div className="flex flex-col justify-center w-full">
                    <CircleSpinner className="mx-auto mt-20" />
                </div>
            )}
            
            {!loading && (
                <ChecksTable
                    className="mt-4"
                    checks={checks?.rows ?? []}
                    checked={checked}
                    allChecked={allChecked}
                    someChecked={someChecked}
                    hasChecked={hasChecked}
                    toggleChecked={toggleChecked}
                    toggleAllChecked={toggleAllChecked}
                    orderField={orderField}
                    orderDirection={orderDirection}
                    onHeaderClick={onHeaderClick}
                    unmappedChecks={unmappedChecks}
                    setMapCheckId={setMapCheckId} />
            )}
            
            {!loading && (<div className="flex justify-center py-4 mx-auto">
                <Pagination
                    count={checks?.count}
                    itemsPerPage={checksPerPage}
                    forcePage={pageIndex}
                    onPageChange={e => {
                        setPageIndex(e.selected)
                        scrollToTop()
                    }} />
            </div>)}
            
            <UploadChecksDialog onClose={fetchChecks} />
            
            <MappingModal
                checkMappings={checkMappings}
                onFinishMapping={onFinishMapping}
                showModal={showMappingsModal}
                showHideModal={setShowMappingsModal}
                source={SOURCE_CHECKS} />
        
        </div>
        
    )
    
}

export default Checks
